import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hostsApi, type GroupParams, type Host, type NodeInfo } from '../../api'
import { t } from '../../i18n'
import { Button, Check, Chip, Field, Icon, Input, Meter, Note, Segmented, Tag } from '../../ui'
import { NODES_KEY, useProvisioning } from '../Nodes'
import { FactorBadge, SecondFactorProvider } from '../SecondFactor'
import { nodePath } from './api'
import { GroupDialog } from './GroupDialog'
import { GroupJournal } from './GroupJournal'
import { GroupBroadcasts, MoveDialog, useGroupRoutes, type GroupRoute } from './GroupOutputs'
import { useRelayLive } from './live'
import { useNames } from './NodeTabs'
import { groupErrorText, groupQueryKey, groupsApi, loadState, memberActions, memberLook, pct, rampStep, storedState, type GroupMemberView, type GroupView } from './groups'
import { FAILOVER_KEY, useFailover } from './NodeFailover'
import { AddNodeDialog, DeleteGroupDialog, ReadinessPanel, RemoveDialog, canRemove } from './GroupCompose'
import { DEFAULT_GROUP_KEY, useDefaultGroup } from './groupAdmin'
import { tn } from './nodeSwitch'

/*
 * Network › Nodes › node group (GROUP-UI; node-group.md §6.4 «what the
 * operator sees»): the group's address and totals, its members with state,
 * load and its main term, share of new connections, share of live
 * publishers, cap, AUTO / MANUAL weight, ramp; the member actions (drain,
 * maintenance, return, end ramp) and the weight in ConfirmDialog with the
 * code; the group's settings. Console density: .sx-console, .sx-table.compact.
 *
 * GROUP-UI-2 (GROUP-3 API): outputs from another member (egress_offload) in
 * the settings, the members' broadcasts and moving their outputs
 * (GroupOutputs.tsx), which members carry other members' outputs (OUTPUTS n),
 * the share each member actually got in the last 15 min, the balance journal
 * (GroupJournal.tsx).
 *
 * GROUP-UI-3: Add node / Remove from group / Delete group (GroupCompose.tsx), the readiness of
 * the group (NG-16 C), the DEFAULT tag of the default group (NG-17).
 */

export function GroupPageRoute() {
  return (
    <SecondFactorProvider>
      <GroupPage />
    </SecondFactorProvider>
  )
}

type Ask =
  | { kind: 'drain' | 'maintenance' | 'return' | 'serve' | 'weight'; m: GroupMemberView }
  | { kind: 'settings'; change: Partial<GroupParams> }
  | { kind: 'move'; r: GroupRoute }
  | { kind: 'remove'; m: GroupMemberView }
  | { kind: 'add' | 'delete' }
  | null

function GroupPage() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: groupQueryKey(id), queryFn: () => groupsApi.get(id), refetchInterval: 5000, retry: false })
  const prov = useProvisioning()
  const live = useRelayLive()
  const names = useNames()
  const [ask, setAsk] = useState<Ask>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const fo = useFailover()
  const dg = useDefaultGroup()
  const [done, setDone] = useState(() => (location.state as { done?: string } | null)?.done ?? '')
  const trigger = useRef<HTMLElement | null>(null)
  const membersOf = q.data?.members ?? []
  const bc = useGroupRoutes(membersOf, live.available === true)
  const hostOf = useMemo(() => {
    const by = new Map<string, Host>()
    for (const h of prov.hosts.data ?? []) if (h.node_id && !h.revoked_at) by.set(h.node_id, h)
    return (node: string) => by.get(node)
  }, [prov.hosts.data])

  const open = (a: Exclude<Ask, null>, el?: HTMLElement | null) => {
    trigger.current = el ?? (document.activeElement as HTMLElement | null)
    setDone('')
    setAsk(a)
  }
  const finish = async (text: string) => {
    setAsk(null)
    setDone(text)
    await Promise.all([
      qc.invalidateQueries({ queryKey: groupQueryKey(id) }),
      qc.invalidateQueries({ queryKey: NODES_KEY }),
      qc.invalidateQueries({ queryKey: FAILOVER_KEY }),
      qc.invalidateQueries({ queryKey: ['relay'] }),
      qc.invalidateQueries({ queryKey: DEFAULT_GROUP_KEY }),
    ])
  }

  if (q.isPending) return <div className="sx-console p-4 text-sm text-muted">…</div>
  if (q.error || !q.data) {
    return (
      <div className="sx-console flex flex-col gap-3" data-testid="group-page">
        <Crumb />
        <Note state="fail" data-testid="group-error">
          {q.error && hostStatus(q.error) === 404 ? t('group.notFound') : groupErrorText(q.error)}
        </Note>
      </div>
    )
  }
  const v = q.data
  const g = v.group
  const p = g.params
  const members = [...v.members].sort((a, b) => a.name.localeCompare(b.name))
  const takes = members.filter((m) => m.takes).length
  const publishers = members.reduce((a, m) => a + m.connections, 0)
  const joining = members.filter((m) => storedState(m) === 'joining')
  const isDefault = !!g.default || (!!dg.data?.group && dg.data.group === g.id)
  const nameOf = (nid?: string) => members.find((m) => m.node === nid)?.name ?? names.nameOf(nid)

  return (
    <div className="sx-console flex flex-col gap-4" data-testid="group-page" data-group={g.id}>
      <Crumb />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[18px] leading-6 font-medium">{tn('group.title', { name: g.name })}</h1>
        {isDefault && <Tag data-testid="group-default-tag">{t('gx.default.tag')}</Tag>}
        <span className="ml-auto">{prov.on && <FactorBadge />}</span>
      </div>

      <section className="sx-panel grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))]" data-testid="group-facts">
        <Fact k={t('group.fact.address')} className="col-span-2 md:col-span-1">
          {g.fqdn ? <AddressCopy value={g.fqdn} /> : <span className="text-muted">{t('group.fact.noZone')}</span>}
        </Fact>
        <Fact k={t('group.fact.members')}>
          <span className="sx-mono" data-testid="group-members-count">{members.length}</span>
        </Fact>
        <Fact k={t('group.fact.take')}>
          <span className="sx-mono" data-testid="group-takes">{takes}</span>
        </Fact>
        <Fact k={t('group.fact.publishers')}>
          <span className="sx-mono">{publishers}</span>
        </Fact>
        <Fact k={t('group.fact.weights')}>
          <span className="inline-flex items-center gap-2">
            <Tag>{t(p?.auto_weights === false ? 'group.tag.manual' : 'group.tag.auto')}</Tag>
            {p && <span className="text-muted">{p.auto_ramp ? tn('group.rampFact', { s: p.ramp_s }) : t('group.rampManual')}</span>}
          </span>
        </Fact>
      </section>

      {done && (
        <Note data-testid="group-done" role="status">
          {done}
        </Note>
      )}

      {v.readiness && <ReadinessPanel r={v.readiness} fqdn={g.fqdn ?? g.name} group={g.name} isDefault={isDefault} nameOf={nameOf} />}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="sx-label">{t('group.fact.members')}</span>
        <Button size="sm" className="max-md:min-h-[44px]" data-testid="group-add-btn" onClick={(e) => open({ kind: 'add' }, e.currentTarget)}>
          {t('gx.add.btn')}
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="flex flex-col gap-3" data-testid="group-empty-box">
          <Note data-testid="group-empty">{t('gx.empty')}</Note>
          <span>
            <Button size="sm" className="max-md:min-h-[44px]" data-testid="group-delete-btn" onClick={(e) => open({ kind: 'delete' }, e.currentTarget)}>
              {t('gx.delete.btn')}
            </Button>
          </span>
        </div>
      ) : (
        <>
          <MembersTable members={members} view={v} hostOf={hostOf} prov={prov.on} open={open} />
          <MemberCards members={members} offload={!!p?.egress_offload} hostOf={hostOf} prov={prov.on} open={open} />
        </>
      )}

      {joining.length > 0 && (
        <section className="sx-panel flex flex-col gap-3" data-testid="group-ramp">
          <span className="sx-label">{t('group.ramp')}</span>
          {joining.map((m) => (
            <div key={m.node} className="grid items-center gap-x-4 gap-y-2 md:grid-cols-[120px_minmax(160px,240px)_1fr_auto]" data-testid={`group-ramp-${m.node}`}>
              <span className="sx-mono font-medium">{m.name}</span>
              <Meter label={t('group.ramp')} used={rampStep(m.ramp)} max={10} count={`${rampStep(m.ramp)}/10`} />
              <span className="sx-cap">{p?.auto_ramp === false ? t('group.ramp.manual') : t('group.ramp.step', { n: rampStep(m.ramp) })}</span>
              <Button size="sm" className="max-md:min-h-[44px]" data-testid={`group-serve-${m.node}`} onClick={(e) => open({ kind: 'serve', m }, e.currentTarget)}>
                {t('group.btn.serve')}
              </Button>
            </div>
          ))}
        </section>
      )}

      <GroupBroadcasts routes={bc.routes} loading={bc.loading} error={live.available === false ? undefined : bc.error} open={(r, el) => open({ kind: 'move', r }, el)} />

      <GroupJournal id={g.id} group={g.name} nameOf={(nid) => nameOf(nid)} />

      {p && <Settings id={g.id} params={p} onAsk={(change, el) => open({ kind: 'settings', change }, el)} />}

      {ask?.kind === 'add' ? (
        <AddNodeDialog group={g} groups={fo.data?.groups ?? []} hostOf={hostOf} prov={prov.on} rampMin={p?.auto_ramp === false ? undefined : Math.round((p?.ramp_s ?? 600) / 60)} dry={dg.supported} trigger={trigger} onClose={() => setAsk(null)} onDone={finish} />
      ) : ask?.kind === 'remove' ? (
        <RemoveDialog m={ask.m} group={g} dry={dg.supported} trigger={trigger} onClose={() => setAsk(null)} onDone={finish} />
      ) : ask?.kind === 'delete' ? (
        <DeleteGroupDialog
          group={g}
          isDefault={isDefault}
          trigger={trigger}
          onClose={() => setAsk(null)}
          onDone={() => {
            setAsk(null)
            void qc.invalidateQueries({ queryKey: FAILOVER_KEY })
            void qc.invalidateQueries({ queryKey: DEFAULT_GROUP_KEY })
            navigate('/admin/network/nodes', { state: { done: t('gx.delete.done', { name: g.name }) } })
          }}
        />
      ) : ask?.kind === 'move' ? (
        <MoveDialog r={ask.r} members={members} group={g.name} trigger={trigger} onClose={() => setAsk(null)} onDone={finish} />
      ) : (
        ask && <AskDialog ask={ask as AskOf} view={v} host={(ask as AskOf).kind !== 'settings' ? hostOf((ask as Exclude<AskOf, { kind: 'settings' }>).m.node) : undefined} trigger={trigger} onClose={() => setAsk(null)} onDone={finish} />
      )}
    </div>
  )
}

const hostStatus = (e: unknown) => (e as { status?: number })?.status

function Crumb() {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-muted">
      <Link to="/admin/network/nodes" className="hover:text-text">
        {t('group.back')}
      </Link>
      <Icon name="chevron-right" size={12} />
    </nav>
  )
}

function Fact({ k, children, className = '' }: { k: string; children: ReactNode; className?: string }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="sx-label">{k}</span>
      <span className="min-w-0 text-[13px]">{children}</span>
    </div>
  )
}

/** The group's address, IngestFields' read-only value with Copy (.sx-combo). */
function AddressCopy({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <span className="sx-combo max-w-[420px]" data-testid="group-address">
      <span className="v truncate" title={value}>
        {value}
      </span>
      <button
        type="button"
        className="copy"
        aria-label={`${t('group.copy')} ${value}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value).catch(() => undefined)
          setCopied(true)
          window.setTimeout(() => setCopied(false), 2000)
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={16} />
        <span aria-live="polite">{t(copied ? 'group.copied' : 'group.copy')}</span>
      </button>
    </span>
  )
}

/* ── members ─────────────────────────────────────────────────────────────── */

function StateCell({ m }: { m: GroupMemberView }) {
  const l = memberLook(m)
  const hint = l.look === 'joining' ? t('group.hint.joining', { n: rampStep(m.ramp) }) : l.hint
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={hint} data-testid={`member-state-${m.node}`} data-look={l.look}>
      <Chip state={l.state} size="sm" still>
        {l.word}
      </Chip>
      {l.look === 'joining' && <Tag>{`${rampStep(m.ramp)}/10`}</Tag>}
      {l.was && <span className="sx-cap">{t('group.was', { state: l.was })}</span>}
    </span>
  )
}

function LoadCell({ m }: { m: GroupMemberView }) {
  const p = Math.round(m.load.load * 100)
  const w = Math.max(0, Math.min(100, p))
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="sx-load" data-state={loadState(m.load.load)} role="meter" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} aria-label={`${t('group.col.load')} ${m.name}`}>
        <i>
          <b style={{ width: `${w}%` }} />
        </i>
        {pct(p)}
      </span>
      {m.load.by && <span className="sx-cap">{t(`group.load.${m.load.by}`)}</span>}
    </span>
  )
}

function WeightCell({ m }: { m: GroupMemberView }) {
  const manual = m.weight_mode === 'manual'
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`member-weight-${m.node}`} data-mode={manual ? 'manual' : 'auto'}>
      <Tag>{t(manual ? 'group.tag.manual' : 'group.tag.auto')}</Tag>
      {manual && <span className="sx-mono text-text-soft">{pct(m.weight_pct)}</span>}
    </span>
  )
}

const CapCell = ({ m }: { m: GroupMemberView }) => (
  <span className={m.cap_held ? 'text-warn' : undefined} title={m.cap_held ? t('group.capHeld') : undefined} data-testid={`member-cap-${m.node}`} data-held={m.cap_held ? 'true' : undefined}>
    {m.cap_pct !== undefined ? pct(m.cap_pct) : '—'}
  </span>
)

/** The share of new connections it actually got in the last 15 min (§6.4); the count in the title. */
const Got = ({ m }: { m: GroupMemberView }) => (
  <span title={t('group.got.hint', { n: m.new_15m ?? 0 })}>{m.share_15m !== undefined ? pct(m.share_15m) : '—'}</span>
)

/**
 * An outputs node of the group (GROUP-3): .sx-tag OUTPUTS n — a neutral fact, not a state —
 * with its caption in words under the name, so the tag never reads as the node's own outputs.
 * With offload on, a member that may send outputs shows it at 0 too. The API's hub role never
 * shows. DS: NG-09 (decided 01.11).
 */
const isOutputsNode = (m: GroupMemberView, offload: boolean) => (m.hub_routes ?? 0) > 0 || (offload && !!m.hub)

function HubTag({ m, offload }: { m: GroupMemberView; offload: boolean }) {
  const n = m.hub_routes ?? 0
  if (!isOutputsNode(m, offload)) return null
  return (
    <Tag data-testid={`member-hub-${m.node}`}>
      {t('group.hub.tag', { n })}
    </Tag>
  )
}

function HubCaption({ m, offload }: { m: GroupMemberView; offload: boolean }) {
  if (!isOutputsNode(m, offload)) return null
  const n = m.hub_routes ?? 0
  return (
    <span className="sx-cap block whitespace-normal" id={`member-hub-cap-${m.node}`} data-testid={`member-hub-cap-${m.node}`}>
      {t(n ? 'group.hub.hint' : 'group.hub.hintIdle', { n })}
    </span>
  )
}

type Open = (a: Exclude<Ask, null>, el?: HTMLElement | null) => void

const BTN: Record<'drain' | 'maintenance' | 'return' | 'serve', Parameters<typeof t>[0]> = {
  drain: 'group.btn.drain',
  maintenance: 'group.btn.maintenance',
  return: 'group.btn.return',
  serve: 'group.btn.serve',
}

function Actions({ m, host, prov, open }: { m: GroupMemberView; host?: Host; prov: boolean; open: Open }) {
  const acts = memberActions(m)
  // drain / return are host commands: a node without a host agent cannot take them
  const noHost = !prov || !host
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
      <Button size="sm" className="max-md:min-h-[44px]" data-testid={`member-weight-btn-${m.node}`} onClick={(e) => open({ kind: 'weight', m }, e.currentTarget)}>
        {t('group.btn.weight')}
      </Button>
      {acts.map((a) => {
        const needsHost = a === 'drain' || a === 'return'
        return (
          <Button
            key={a}
            size="sm"
            className="max-md:min-h-[44px]"
            data-testid={`member-${a}-${m.node}`}
            disabled={needsHost && noHost}
            title={needsHost && noHost ? t('group.err.noHost', { name: m.name }) : undefined}
            onClick={(e) => open({ kind: a, m }, e.currentTarget)}
          >
            {t(BTN[a])}
          </Button>
        )
      })}
      {canRemove(m) && (
        <Button size="sm" className="max-md:min-h-[44px]" data-testid={`member-remove-${m.node}`} onClick={(e) => open({ kind: 'remove', m }, e.currentTarget)}>
          {t('gx.remove.btn')}
        </Button>
      )}
    </span>
  )
}

function MembersTable({ members, view, hostOf, prov, open }: { members: GroupMemberView[]; view: GroupView; hostOf: (n: string) => Host | undefined; prov: boolean; open: Open }) {
  return (
    <section className="sx-panel hidden overflow-x-auto !p-0 md:block" aria-label={t('group.fact.members')} data-testid="group-members" data-fallback={view.fallback ? 'true' : undefined}>
      <table className="sx-table compact">
        <thead>
          <tr>
            <th scope="col">{t('group.col.member')}</th>
            <th scope="col">{t('group.col.state')}</th>
            <th scope="col">{t('group.col.load')}</th>
            <th scope="col" className="num">{t('group.col.share')}</th>
            <th scope="col" className="num">{t('group.col.got')}</th>
            <th scope="col" className="num">{t('group.col.live')}</th>
            <th scope="col" className="num">{t('group.col.cap')}</th>
            <th scope="col">{t('group.col.weight')}</th>
            <th scope="col" className="num">{t('group.col.publishers')}</th>
            <th scope="col" className="num"><span className="sr-only">{t('group.col.actions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.node} data-testid={`member-row-${m.node}`} data-state={storedState(m)}>
              <td>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <Link to={nodePath(m.node)} className="nm hover:underline">
                    <Icon name="node" size={16} className="text-text-soft" />
                    <span className="sx-mono">{m.name}</span>
                  </Link>
                  <HubTag m={m} offload={!!view.group.params?.egress_offload} />
                </span>
                <HubCaption m={m} offload={!!view.group.params?.egress_offload} />
              </td>
              <td><StateCell m={m} /></td>
              <td><LoadCell m={m} /></td>
              <td className="num" data-testid={`member-share-${m.node}`}>{pct(m.share)}</td>
              <td className="num" data-testid={`member-got-${m.node}`}><Got m={m} /></td>
              <td className="num">{pct(m.conn_share)}</td>
              <td className="num"><CapCell m={m} /></td>
              <td><WeightCell m={m} /></td>
              <td className="num">{m.connections}</td>
              <td className="num"><Actions m={m} host={hostOf(m.node)} prov={prov} open={open} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

/** Phone: one card per member, state first (ConsoleNodeTable on phones: cards). */
function MemberCards({ members, offload, hostOf, prov, open }: { members: GroupMemberView[]; offload: boolean; hostOf: (n: string) => Host | undefined; prov: boolean; open: Open }) {
  return (
    <ul className="sx-panel flex flex-col !p-0 md:hidden" data-testid="group-member-cards">
      {members.map((m) => (
        <li key={m.node} className="flex flex-col gap-2 border-b border-line px-3 py-3 last:border-b-0" data-testid={`member-card-${m.node}`}>
          <span className="flex items-center justify-between gap-2">
            <Link to={nodePath(m.node)} className="inline-flex min-h-[44px] items-center gap-2 font-medium">
              <Icon name="node" size={16} className="text-text-soft" />
              <span className="sx-mono">{m.name}</span>
              <HubTag m={m} offload={offload} />
            </Link>
            <StateCell m={m} />
          </span>
          <HubCaption m={m} offload={offload} />
          <LoadCell m={m} />
          <span className="grid grid-cols-4 gap-2 text-[12px]">
            <MiniFact k={t('group.col.share')} v={pct(m.share)} />
            <MiniFact k={t('group.col.got')} v={<Got m={m} />} />
            <MiniFact k={t('group.col.live')} v={pct(m.conn_share)} />
            <MiniFact k={t('group.col.cap')} v={<CapCell m={m} />} />
          </span>
          <span className="flex items-center justify-between gap-2">
            <WeightCell m={m} />
            <span className="sx-mono text-muted">
              {t('group.col.publishers')} {m.connections}
            </span>
          </span>
          <Actions m={m} host={hostOf(m.node)} prov={prov} open={open} />
        </li>
      ))}
    </ul>
  )
}

const MiniFact = ({ k, v }: { k: string; v: ReactNode }) => (
  <span className="flex flex-col gap-0.5">
    <span className="sx-label">{k}</span>
    <span className="sx-mono">{v}</span>
  </span>
)

/* ── settings ────────────────────────────────────────────────────────────── */

function Settings({ id, params, onAsk }: { id: string; params: GroupParams; onAsk: (change: Partial<GroupParams>, el: HTMLElement) => void }) {
  const [aw, setAw] = useState(params.auto_weights)
  const [ar, setAr] = useState(params.auto_ramp)
  const [off, setOff] = useState(!!params.egress_offload)
  const [ramp, setRamp] = useState(String(params.ramp_s))
  const [drain, setDrain] = useState(String(Math.round(params.drain_deadline_s / 60)))
  const key = `${id}|${params.auto_weights}|${params.auto_ramp}|${params.ramp_s}|${params.drain_deadline_s}|${params.egress_offload}`
  useEffect(() => {
    setOff(!!params.egress_offload)
    setAw(params.auto_weights)
    setAr(params.auto_ramp)
    setRamp(String(params.ramp_s))
    setDrain(String(Math.round(params.drain_deadline_s / 60)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  const rampN = Number(ramp)
  const drainN = Number(drain)
  const rampOk = Number.isInteger(rampN) && rampN >= 60 && rampN <= 3600
  const drainOk = Number.isInteger(drainN) && drainN >= 1 && drainN <= 1440
  const change: Partial<GroupParams> = {}
  if (aw !== params.auto_weights) change.auto_weights = aw
  if (ar !== params.auto_ramp) change.auto_ramp = ar
  if (off !== !!params.egress_offload) change.egress_offload = off
  if (rampOk && rampN !== params.ramp_s) change.ramp_s = rampN
  if (drainOk && drainN * 60 !== params.drain_deadline_s) change.drain_deadline_s = drainN * 60
  const dirty = Object.keys(change).length > 0
  return (
    <section className="sx-panel flex flex-col gap-4" data-testid="group-settings">
      <span className="sx-label">{t('group.settings')}</span>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          <Check checked={aw} onChange={setAw} label={t('group.set.autoWeights')} hint={t('group.set.autoWeightsHint')} testId="group-set-auto-weights" />
          <Check checked={ar} onChange={setAr} label={t('group.set.autoRamp')} hint={t('group.set.autoRampHint')} testId="group-set-auto-ramp" />
          <Check checked={off} onChange={setOff} label={t('group.set.offload')} hint={t('group.set.offloadHint')} testId="group-set-offload" />
        </div>
        <div className="flex flex-col gap-3">
          <Field id="group-ramp-s" label={t('group.set.ramp')} help={t('group.set.rampHelp')} error={rampOk ? undefined : t('group.err.range')}>
            <Input id="group-ramp-s" inputMode="numeric" value={ramp} disabled={!ar} onChange={(e) => setRamp(e.target.value.replace(/\D/g, ''))} data-testid="group-set-ramp" className="max-w-[160px]" />
          </Field>
          <Field id="group-drain-min" label={t('group.set.drain')} help={t('group.set.drainHelp')} error={drainOk ? undefined : t('group.err.range')}>
            <Input id="group-drain-min" inputMode="numeric" value={drain} onChange={(e) => setDrain(e.target.value.replace(/\D/g, ''))} data-testid="group-set-drain" className="max-w-[160px]" />
          </Field>
        </div>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" disabled={!dirty || !rampOk || !drainOk} data-testid="group-settings-save" onClick={(e) => onAsk(change, e.currentTarget)}>
          {t('group.btn.saveSettings')}
        </Button>
      </div>
    </section>
  )
}

/* ── the confirmations ───────────────────────────────────────────────────── */

type AskOf = Exclude<Ask, null | { kind: 'move' } | { kind: 'remove' } | { kind: 'add' | 'delete' }>

function AskDialog({ ask, view, host, trigger, onClose, onDone }: { ask: AskOf; view: GroupView; host?: Host; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: (text: string) => Promise<void> }) {
  const g = view.group
  const p = g.params
  if (ask.kind === 'settings') {
    const yes = (b: boolean) => t(b ? 'group.on' : 'group.off')
    const parts: string[] = []
    const c = ask.change
    if (c.auto_weights !== undefined) parts.push(`${t('group.set.autoWeights')} — ${yes(c.auto_weights)}`)
    if (c.auto_ramp !== undefined) parts.push(`${t('group.set.autoRamp')} — ${yes(c.auto_ramp)}`)
    if (c.egress_offload !== undefined) parts.push(`${t('group.set.offload')} — ${yes(c.egress_offload)}`)
    if (c.ramp_s !== undefined) parts.push(`${t('group.set.ramp')} ${c.ramp_s}`)
    if (c.drain_deadline_s !== undefined) parts.push(`${t('group.set.drain')} ${c.drain_deadline_s / 60}`)
    return (
      <GroupDialog
        testId="group-settings"
        title={tn('group.confirm.settings', { name: g.name })}
        facts={[t('group.set.changes', { list: parts.join('; ') }), ...(c.egress_offload !== undefined ? [t('group.set.offloadNew')] : [])]}
        action={t('group.btn.saveSettings')}
        returnFocus={trigger}
        onClose={onClose}
        errorText={(e) => groupErrorText(e)}
        submit={async (totp) => {
          await groupsApi.params(g.id, c, { totp })
          await onDone(t('group.done.settings', { name: g.name }))
        }}
      />
    )
  }
  const m = ask.m
  const err = (e: unknown) => groupErrorText(e, { name: m.name })
  switch (ask.kind) {
    case 'drain': {
      const min = Math.round((p?.drain_deadline_s ?? 3600) / 60)
      return (
        <GroupDialog
          testId="member-drain"
          title={tn('group.confirm.drain', { name: m.name })}
          facts={[
            t('group.drain.noNew'),
            tn('group.drain.left', { n: m.connections, min }),
            t('group.drain.apps'),
            ...(m.hub_routes ? [tn('group.drain.hub', { n: m.hub_routes, min })] : []),
          ]}
          action={t('group.btn.drain')}
          returnFocus={trigger}
          onClose={onClose}
          errorText={err}
          submit={async (totp) => {
            await hostCommand(host, { kind: 'drain', args: { deadline_s: min * 60 }, totp }, m.name)
            await onDone(t('group.done.drain', { name: m.name }))
          }}
        />
      )
    }
    case 'return':
      return (
        <GroupDialog
          testId="member-return"
          title={tn('group.confirm.return', { name: m.name, group: g.name })}
          facts={[p?.auto_ramp === false ? t('group.return.manual') : tn('group.return.ramp', { min: Math.round((p?.ramp_s ?? 600) / 60) }), t('group.return.agent', { name: m.name })]}
          action={t('group.btn.return')}
          code={false}
          returnFocus={trigger}
          onClose={onClose}
          errorText={err}
          submit={async () => {
            await hostCommand(host, { kind: 'undrain' }, m.name)
            await onDone(t('group.done.return', { name: m.name }))
          }}
        />
      )
    case 'maintenance':
      return (
        <GroupDialog
          testId="member-maintenance"
          title={tn('group.confirm.maintenance', { name: m.name })}
          facts={[t('group.maint.empty'), t('group.maint.down')]}
          action={t('group.btn.maintenance')}
          returnFocus={trigger}
          onClose={onClose}
          errorText={err}
          submit={async (totp) => {
            await groupsApi.action(m.node, 'maintenance', { totp })
            await onDone(t('group.done.maintenance', { name: m.name }))
          }}
        />
      )
    case 'serve':
      return (
        <GroupDialog
          testId="member-serve"
          title={tn('group.confirm.serve', { name: m.name })}
          facts={[t('group.serve.full')]}
          action={t('group.btn.serve')}
          returnFocus={trigger}
          onClose={onClose}
          errorText={err}
          submit={async (totp) => {
            await groupsApi.action(m.node, 'serve', { totp })
            await onDone(t('group.done.serve', { name: m.name }))
          }}
        />
      )
    case 'weight':
      return <WeightDialog m={m} autoWeights={p?.auto_weights !== false} trigger={trigger} onClose={onClose} onDone={onDone} />
  }
}

/** drain / undrain go to the node's host agent (MOVE-1); the member state follows the order. */
async function hostCommand(host: Host | undefined, body: Parameters<typeof hostsApi.command>[1], name: string) {
  if (!host) throw new Error(t('group.err.noHost', { name }))
  const c = await hostsApi.command(host.id, body)
  if (c.state === 'failed') throw new Error(c.error || 'failed')
}

function WeightDialog({ m, autoWeights, trigger, onClose, onDone }: { m: GroupMemberView; autoWeights: boolean; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: (text: string) => Promise<void> }) {
  const [mode, setMode] = useState<'auto' | 'manual'>(m.weight_mode === 'manual' || !autoWeights ? 'manual' : 'auto')
  const [share, setShare] = useState(m.weight_pct !== undefined ? String(m.weight_pct) : '')
  const [cap, setCap] = useState(m.cap_pct !== undefined ? String(m.cap_pct) : '')
  const shareN = share === '' ? undefined : Number(share)
  const capN = cap === '' ? undefined : Number(cap)
  const shareOk = mode === 'auto' || (shareN !== undefined && shareN >= 0 && shareN <= 100)
  const capOk = capN === undefined || (capN > 0 && capN <= 100)
  const over = mode === 'manual' && shareN !== undefined && capN !== undefined && shareN > capN
  return (
    <GroupDialog
      testId="member-weight"
      title={tn('group.confirm.weight', { name: m.name })}
      action={t('group.btn.saveWeight')}
      ready={shareOk && capOk && !over}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => groupErrorText(e, { name: m.name, share: shareN, cap: capN })}
      submit={async (totp) => {
        await groupsApi.member(m.node, { weight_mode: mode, weight_pct: mode === 'manual' ? (shareN ?? 0) : null, cap_pct: capN ?? null, totp })
        await onDone(t('group.done.weight', { name: m.name }))
      }}
    >
      <div className="sx-field">
        <span className="lbl" id="member-weight-mode">{t('group.weight.mode')}</span>
        <Segmented
          label={t('group.weight.mode')}
          value={mode}
          onChange={setMode}
          disabled={autoWeights ? [] : ['auto']}
          options={[
            { value: 'auto', label: t('group.weight.auto'), testId: 'member-weight-auto' },
            { value: 'manual', label: t('group.weight.manual'), testId: 'member-weight-manual' },
          ]}
        />
        {!autoWeights && <span className="help">{t('group.weight.autoOff')}</span>}
      </div>
      {mode === 'manual' && (
        <Field id="member-weight-share" label={t('group.weight.share')} help={t('group.weight.shareHelp')} error={shareOk ? undefined : t('group.err.needShare')}>
          <Input id="member-weight-share" inputMode="decimal" value={share} onChange={(e) => setShare(e.target.value.replace(/[^\d.]/g, ''))} data-testid="member-weight-share" />
        </Field>
      )}
      <Field
        id="member-weight-cap"
        label={t('group.weight.cap')}
        help={t('group.weight.capHelp')}
        error={!capOk ? t('group.err.pct') : over ? t('group.err.overCap', { share: shareN!, cap: capN! }) : undefined}
      >
        <Input id="member-weight-cap" inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value.replace(/[^\d.]/g, ''))} data-testid="member-weight-cap" />
      </Field>
    </GroupDialog>
  )
}

/* ── node card: the private address (GROUP-2c) ───────────────────────────── */

/** PATCH /api/admin/nodes/{id} {private_addr}: no second factor (an address the peers learn, nothing on air changes). */
export function PrivateAddressPanel({ n }: { n: NodeInfo }) {
  const qc = useQueryClient()
  const [v, setV] = useState(n.private_addr ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')
  useEffect(() => setV(n.private_addr ?? ''), [n.private_addr])
  const dirty = v.trim() !== (n.private_addr ?? '')
  const save = async () => {
    setBusy(true)
    setErr('')
    setDone('')
    try {
      await groupsApi.privateAddr(n.id, v.trim())
      setDone(t('group.done.addr', { name: n.name }))
      await qc.invalidateQueries({ queryKey: NODES_KEY })
    } catch (e) {
      setErr(groupErrorText(e, { name: n.name }))
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="sx-panel sx-console flex flex-col gap-3" data-testid="node-private-addr">
      <div className="flex flex-wrap items-end gap-2">
        <Field id={`private-addr-${n.id}`} label={t('group.privateAddr')} help={t('group.privateAddrHelp')} error={err || undefined} className="min-w-[220px] flex-1">
          <Input id={`private-addr-${n.id}`} value={v} placeholder="10.0.0.2" spellCheck={false} onChange={(e) => setV(e.target.value)} data-testid="node-private-addr-input" />
        </Field>
        <Button disabled={!dirty || busy} busy={busy} data-testid="node-private-addr-save" onClick={() => void save()}>
          {t('group.btn.saveAddr')}
        </Button>
      </div>
      {done && (
        <Note role="status" data-testid="node-private-addr-done">
          {done}
        </Note>
      )}
    </section>
  )
}
